/* istanbul ignore file - need to fix flakey tests */
import { Dialog, Stack } from '@mui/material';
import { Button } from '@partful/shared/ui/Button';
import { DialogContent, DialogFooter, DialogHeader, DialogPanel } from '@partful/shared/ui/Dialog/Dialog';
import { Option, Select } from '@partful/shared/ui/Select';
import { Typography } from '@partful/shared/ui/Typography';
import { CategoryNode } from '@services/productInventory';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ulid } from 'ulid';
import styles from './MovePartDialog.module.scss';

interface MovePartDialogProps {
  selectedRowsToMove?: CategoryNode[];
  data: CategoryNode[];
  onMovePart: (selectedOption: CategoryNode) => void;
  onCancel: () => void;
}

export function MovePartDialog({ selectedRowsToMove, data, onMovePart, onCancel }: MovePartDialogProps) {
  const { t: i18nCommon } = useTranslation('common');
  const { t: i18n } = useTranslation('manageProduct');
  const [selectedOption, setSelectedOption] = useState<string>('');

  const options = useMemo(
    () =>
      data.length !== 0
        ? data
            .filter((node) => node.isCategory && !selectedRowsToMove?.some((row) => row.PID === node.PID))
            .map((t) => ({
              id: t.PID,
              name: t.name,
            }))
        : [
            {
              id: ulid(),
              name: i18n('partsCategories.interactive.noAvailableCategoriesOrAssemblies'),
            },
          ],
    [data, i18n, selectedRowsToMove]
  );

  const onMovePartClick = () => {
    const selectedTreeNode = data.find((t) => t.PID === selectedOption);
    onMovePart(selectedTreeNode!);
    setSelectedOption('');
  };

  const firstSelectedRow = selectedRowsToMove?.[0];
  const multipleRowsSelected = selectedRowsToMove && selectedRowsToMove.length > 1;

  return (
    <Dialog
      open={Boolean(selectedRowsToMove?.length)}
      onClose={onCancel}
      PaperProps={{
        className: styles.dialogShadowDelete,
      }}
      data-testid="move-part-dialog"
    >
      <DialogPanel>
        <DialogHeader>
          <Typography appearance="heading5">
            {i18n('partsCategories.interactive.moveItemTo', { count: selectedRowsToMove?.length })}
          </Typography>
        </DialogHeader>
        <DialogContent className={styles.dialogContentSpacing}>
          <Typography as="p">
            <Trans>
              {multipleRowsSelected
                ? i18n('partsCategories.interactive.moveMultiplePartsContent', {
                    count: selectedRowsToMove.length,
                  })
                : i18n(
                    firstSelectedRow?.itemType === 'Category'
                      ? 'partsCategories.interactive.moveCategoryContent'
                      : 'partsCategories.interactive.moveItemContent',
                    { name: firstSelectedRow?.name, partNumber: firstSelectedRow?.partNumber }
                  )}
            </Trans>
          </Typography>
          <Stack sx={{ marginTop: '16px' }}>
            <Select
              placeholder={i18n('partsCategories.interactive.moveToPlaceholder')}
              value={selectedOption}
              onChange={/* istanbul ignore next */ (e) => setSelectedOption(e.target.value)}
            >
              {options.map(({ id, name }) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select>
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button onClick={onCancel} variant="tertiary">
            {i18nCommon('cancel')}
          </Button>
          <Button onClick={onMovePartClick} variant="primary" disabled={!selectedOption}>
            {i18n('partsCategories.interactive.moveTo')}
          </Button>
        </DialogFooter>
      </DialogPanel>
    </Dialog>
  );
}
